import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes, BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import { Box, CircularProgress } from '@mui/material';
import customTheme from './constants/customTheme';
import Header from './components/Header';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import QuoterPage from './pages/QuoterPage';
import About from './components/About';
import CruisingSailsPage from './components/CruisingSailsPage';
import PerformanceSailsPage from './components/PerformanceSailsPage';
import DownwindSailsPage from './components/DownwindSailsPage';
import CodeZeroSailsPage from './components/CodeZeroSailsPage';
import SailClothPage from './components/SailClothPage';
import Contact from './components/Contact';
import FeaturedProjectsPage from './components/FeaturedProjectsPage';
import PrivacyPolicy from './components/PrivacyPolicy';
import ResellerProgram from './components/ResellerProgram';
import ClubBenefitPage from './pages/ClubBenefitPage';
import MarinaBenefitPage from './pages/MarinaBenefitPage';
import { QuoteUpdateProvider } from './hooks/quoter/QuoteUpdateContext';
import { useAuth } from './hooks/auth/useAuth';
import { sessionService } from './utils/api/sessionService';
import { CartProvider } from './utils/cart/CartContext';
import ReloginDialog from './components/auth/ReloginDialog';
import { getPageTitle } from './utils/environment';
import NotFoundPage from './pages/NotFoundPage';

// Lazy-loaded components (code-split for better performance)
// These are only loaded when the user navigates to these routes
const UserProfilePage = lazy(() => import('./pages/UserProfilePage'));
const SailOrderingProcess = lazy(() => import('./pages/SailOrderingProcess'));
const WarrantyPage = lazy(() => import('./pages/WarrantyPage'));
const MainsailMeasurementForm = lazy(() => import('./components/MainsailMeasurementForm'));
const IMFMainsailMeasurementForm = lazy(() => import('./components/IMFMainsailMeasurementForm'));
const FurlingHeadsailMeasurementForm = lazy(() => import('./components/FurlingHeadsailMeasurementForm'));
const NonFurlingHeadsailMeasurementForm = lazy(() => import('./components/NonFurlingHeadsailMeasurementForm'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const AdminReset = lazy(() => import('./components/auth/AdminReset'));
const PrintableMeasurementForm = lazy(() => import('./pages/PrintableMeasurementForm'));
const QuoteApprovedPage = lazy(() => import('./pages/QuoteApprovedPage'));
const ApproveQuoteRedirect = lazy(() => import('./pages/ApproveQuoteRedirect'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const OrderConfirmation = lazy(() => import('./components/checkout/OrderConfirmation'));

// Dashboard components (lazy-loaded)
const DashboardRouter = lazy(() => import('./components/dashboards/DashboardRouter').then(module => ({ default: module.default })));
const UserDashboard = lazy(() => import('./components/dashboards/UserDashboard/UserDashboard'));
const ResellerDashboard = lazy(() => import('./components/dashboards/ResellerDashboard/ResellerDashboard'));
const UnifiedDashboard = lazy(() => import('./components/dashboards/UnifiedDashboard/UnifiedDashboard'));

// Admin components (lazy-loaded)
const ClothDownwindAlternateConfig = lazy(() => import('./admin/ClothDownwindAlternateConfig'));
const ClothDownwindDefaultConfig = lazy(() => import('./admin/ClothDownwindDefaultConfig'));
const ClothAlternateConfig = lazy(() => import('./admin/ClothAlternateConfig'));
const ClothDefaultConfig = lazy(() => import('./admin/ClothDefaultConfig'));
const ConfigSettings = lazy(() => import('./admin/ConfigSettings'));
const FeaturedProjectsAdmin = lazy(() => import('./admin/FeaturedProjectsAdmin'));
const PreApprovedResellers = lazy(() => import('./admin/PreApprovedResellers'));
const AuditIndex = lazy(() => import('./admin/AuditIndex'));
const DiscountProgramsIndex = lazy(() => import('./admin/DiscountProgramsIndex'));
const ClothConfigIndex = lazy(() => import('./admin/ClothConfigIndex'));

// Dev tools (lazy-loaded, admin-only)
const DevToolsIndex = lazy(() => import('./pages/dev/DevToolsIndex'));
const PricingViewsTest = lazy(() => import('./pages/dev/PricingViewsTest'));

// Loading fallback component
const LoadingFallback = () => (
    <Box
        sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
            width: '100%'
        }}
    >
        <CircularProgress size={60} />
    </Box>
);

const ReloginDialogWrapper = () => {
    const { requiresRelogin, clearReloginFlag } = useAuth();

    return (
        <ReloginDialog
            open={requiresRelogin}
            onClose={clearReloginFlag}
        />
    );
};

const App: React.FC = () => {

    useEffect(() => {
        // Set document title based on environment
        document.title = getPageTitle();
        
        const pingDb = async () => {
            try {
                const success = await sessionService.pingServer();
                if (!success) {
                    console.warn('Initial database ping failed');
                }
            } catch (error) {
                console.error('Error pinging database:', error);
            }
        };

        pingDb();
    }, []);

    return (
        <HelmetProvider>
            <ThemeProvider theme={customTheme}>
                <CssBaseline />
                <GlobalStyles
                    styles={{
                        '@media print': {
                            // Hide elements marked as no-print
                            '.no-print': {
                                display: 'none !important'
                            },
                            // Hide the main app container (header, main, footer)
                            '#root > .flex': {
                                display: 'none !important'
                            },
                            // Hide the environment indicator (DEV ENVIRONMENT banner)
                            '.environment-indicator': {
                                display: 'none !important'
                            },
                            // Hide MUI AppBar (header)
                            '.MuiAppBar-root': {
                                display: 'none !important'
                            },
                            // Hide MUI drawer and backdrop
                            '.MuiDrawer-root': {
                                display: 'none !important'
                            },
                            '.MuiBackdrop-root': {
                                display: 'none !important'
                            },
                            // Reset body styles for print
                            'body': {
                                margin: 0,
                                padding: 0,
                                background: 'white !important',
                                overflow: 'visible !important'
                            },
                            // Make sure the print modal dialog is visible and positioned correctly
                            '.print-modal.MuiDialog-root': {
                                position: 'static !important',
                                display: 'block !important'
                            },
                            '.print-modal .MuiDialog-container': {
                                position: 'static !important',
                                transform: 'none !important',
                                height: 'auto !important'
                            },
                            '.print-modal .MuiPaper-root': {
                                position: 'static !important',
                                margin: '0 !important',
                                maxHeight: 'none !important',
                                transform: 'none !important'
                            },
                            // Ensure print content is visible
                            '.printable-content': {
                                display: 'block !important',
                                visibility: 'visible !important'
                            }
                        }
                    }}
                />
                <CartProvider>
                    <Router>
                        <div className="flex flex-col min-h-screen">
                            <Header />
                            <main className="flex-grow">
                                <Suspense fallback={<LoadingFallback />}>
                                    <Routes>
                                        {/* Quoter routes with provider */}
                                        <Route
                                            path="/quoter/*"
                                            element={
                                                <QuoteUpdateProvider>
                                                    <QuoterPage />
                                                </QuoteUpdateProvider>
                                            }
                                        />
                                        
                                        {/* Core routes - loaded immediately */}
                                        <Route path="/" element={<HomePage />} />
                                        <Route path="/about" element={<About />} />
                                        <Route path="/cruising-sails" element={<CruisingSailsPage />} />
                                        <Route path="/performance-sails" element={<PerformanceSailsPage />} />
                                        <Route path="/downwind-sails" element={<DownwindSailsPage />} />
                                        <Route path="/code-zero-sails" element={<CodeZeroSailsPage />} />
                                        <Route path="/sail-cloth" element={<SailClothPage />} />
                                        <Route path="/Contact" element={<Contact />} />
                                        <Route path="/FeaturedProjectsPage" element={<FeaturedProjectsPage />} />
                                        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                                        <Route path="/reseller-program" element={<ResellerProgram />} />
                                        <Route path="/club-benefit" element={<ClubBenefitPage />} />
                                        <Route path="/marina-benefit" element={<MarinaBenefitPage />} />
                                        
                                        {/* Lazy-loaded routes - only loaded when accessed */}
                                        <Route path="/checkout" element={<CheckoutPage />} />
                                        <Route path="/checkout/confirmation" element={<OrderConfirmation />} />
                                        <Route path="/profile" element={<UserProfilePage />} />
                                        <Route path="/sail-ordering-process" element={<SailOrderingProcess />} />
                                        <Route path="/warranty" element={<WarrantyPage />} />
                                        
                                        {/* Dashboard routes - lazy loaded */}
                                        <Route path="/dashboard" element={<DashboardRouter />} />
                                        <Route path="/user/dashboard" element={<UserDashboard />} />
                                        <Route path="/reseller/dashboard" element={<ResellerDashboard />} />
                                        <Route path="/admin/dashboard" element={<UnifiedDashboard />} />
                                        
                                        {/* Measurement forms - lazy loaded */}
                                        <Route path="/measurement/mainsail" element={<MainsailMeasurementForm />} />
                                        <Route path="/measurement/imf-mainsail" element={<IMFMainsailMeasurementForm />} />
                                        <Route path="/measurement/furling-headsail" element={<FurlingHeadsailMeasurementForm />} />
                                        <Route path="/measurement/nonfurling-headsail" element={<NonFurlingHeadsailMeasurementForm />} />
                                        <Route path="/measurement/print/:measurementFormId" element={<PrintableMeasurementForm />} />
                                        
                                        {/* Admin routes - lazy loaded */}
                                        <Route path="/admin/cloth-downwind-alternate-config" element={<ClothDownwindAlternateConfig />} />
                                        <Route path="/admin/cloth-downwind-default-config" element={<ClothDownwindDefaultConfig />} />
                                        <Route path="/admin/cloth-alternate-config" element={<ClothAlternateConfig />} />
                                        <Route path="/admin/cloth-default-config" element={<ClothDefaultConfig />} />
                                        <Route path="/admin/config-settings" element={<ConfigSettings />} />
                                        <Route path="/admin/featured-projects" element={<FeaturedProjectsAdmin />} />
                                        <Route path="/admin/pre-approved-resellers" element={<PreApprovedResellers />} />
                                        <Route path="/admin/audit" element={<AuditIndex />} />
                                        <Route path="/admin/discount-programs" element={<DiscountProgramsIndex />} />
                                        <Route path="/admin/cloth-config" element={<ClothConfigIndex />} />
                                        
                                        {/* Dev tools routes - admin only */}
                                        <Route path="/dev" element={<DevToolsIndex />} />
                                        <Route path="/dev/pricing-views" element={<PricingViewsTest />} />
                                        
                                        {/* Quote approval routes - public, no auth required */}
                                        <Route path="/quote-approved" element={<QuoteApprovedPage />} />
                                        <Route path="/approve-quote/:orderId" element={<ApproveQuoteRedirect />} />
                                        
                                        {/* Auth routes - lazy loaded */}
                                        <Route path="/reset-password" element={<ResetPasswordPage />} />
                                        <Route path="/admin/reset-password" element={<AdminReset />} />
                                        
                                        {/* Catch-all 404 route - must be last */}
                                        <Route path="*" element={<NotFoundPage />} />
                                    </Routes>
                                </Suspense>
                            </main>
                            <Footer />
                        </div>
                    </Router>
                </CartProvider>
            </ThemeProvider>
            <ReloginDialogWrapper />
        </HelmetProvider>
    );
};

export default App;
